import React, { useEffect, useState, useMemo } from "react";
import {
  Flex,
  Box,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  IconButton,
  useDisclosure,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  HStack,
  Badge,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useToast,
  useColorModeValue,
  Spinner,
} from "@chakra-ui/react";
import { AddIcon, EditIcon, DeleteIcon, SearchIcon, ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import CardHeader from "components/Card/CardHeader.js";
import {
  getUPI,
  addUPI,
  updateUPI,
  deleteUPI,
  getPropinsi,
  getKota,
} from "services/master/upiService";

export default function MasterUPI() {
  const [data, setData] = useState([]);
  const [propinsiList, setPropinsiList] = useState([]);
  const [kotaList, setKotaList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [editing, setEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const toast = useToast();
  const textColor = useColorModeValue("gray.700", "white");

  const initialForm = {
    UPI_NAMA: "",
    UPI_ALAMAT: "",
    UPI_PROP: "",
    UPI_KOTA: "",
    UPI_KODEPOS: "",
    UPI_NEG: "ID",
    UPI_TELP: "",
    UPI_STATUS: "1",
  };
  const [form, setForm] = useState(initialForm);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await getUPI();
      setData(res.data || []);
    } catch (err) {
      toast({
        title: "Gagal memuat data UPI",
        description: err.response?.data?.message || err.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchDropdowns = async () => {
    try {
      const [resProp, resKota] = await Promise.all([getPropinsi(), getKota()]);
      setPropinsiList(resProp.data || []);
      setKotaList(resKota.data || []);
    } catch (err) {
      console.error("Gagal memuat referensi propinsi/kota", err);
    }
  };

  useEffect(() => {
    fetchData();
    fetchDropdowns();
  }, []);

  // Filter Kota berdasarkan Provinsi yang dipilih di Form
  const filteredKotaOptions = useMemo(() => {
    if (!form.UPI_PROP) return kotaList;
    return kotaList.filter((k) => String(k.KODE_PROPINSI) === String(form.UPI_PROP));
  }, [kotaList, form.UPI_PROP]);

  // Filter Search
  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) return data;
    const lower = searchTerm.toLowerCase();
    return data.filter((item) => {
      const nama = item.UPI_NAMA ? item.UPI_NAMA.toLowerCase() : "";
      const alamat = item.UPI_ALAMAT ? item.UPI_ALAMAT.toLowerCase() : "";
      const prop = (item.provinsi?.URAIAN_PROPINSI || item.UPI_PROP || "").toLowerCase();
      const kota = (item.kota?.URAIAN_KOTA || item.UPI_KOTA || "").toLowerCase();
      return (
        nama.includes(lower) ||
        alamat.includes(lower) ||
        prop.includes(lower) ||
        kota.includes(lower)
      );
    });
  }, [data, searchTerm]);

  // Pagination calculation
  const totalPages = Math.max(1, Math.ceil(filteredData.length / pageSize));
  const currentPagedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, currentPage, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, pageSize]);

  const openAdd = () => {
    setForm(initialForm);
    setEditing(false);
    setEditingId(null);
    onOpen();
  };

  const openEdit = (row) => {
    setForm({
      UPI_NAMA: row.UPI_NAMA || "",
      UPI_ALAMAT: row.UPI_ALAMAT || "",
      UPI_PROP: row.UPI_PROP || "",
      UPI_KOTA: row.UPI_KOTA || "",
      UPI_KODEPOS: row.UPI_KODEPOS || "",
      UPI_NEG: row.UPI_NEG || "ID",
      UPI_TELP: row.UPI_TELP || "",
      UPI_STATUS: row.UPI_STATUS !== undefined && row.UPI_STATUS !== null ? String(row.UPI_STATUS) : "1",
    });
    setEditing(true);
    setEditingId(row.UPI_ID);
    onOpen();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "UPI_PROP") {
      // Reset kota jika propinsi berganti
      setForm((prev) => ({ ...prev, UPI_PROP: value, UPI_KOTA: "" }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const currentUser = localStorage.getItem("username") || localStorage.getItem("nip") || "1";
    try {
      if (editing && editingId) {
        const payload = {
          ...form,
          USER_UPDATE: currentUser,
        };
        await updateUPI(editingId, payload);
        toast({
          title: "Data UPI berhasil diubah",
          status: "success",
          duration: 2000,
          isClosable: true,
        });
      } else {
        const payload = {
          ...form,
          USER_CREATE: currentUser,
        };
        await addUPI(payload);
        toast({
          title: "Data UPI berhasil ditambah",
          status: "success",
          duration: 2000,
          isClosable: true,
        });
      }
      onClose();
      fetchData();
    } catch (err) {
      toast({
        title: "Gagal menyimpan",
        description: err.response?.data?.message || err.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(`Yakin ingin menghapus data UPI ini?`)) return;
    try {
      await deleteUPI(id);
      toast({
        title: "Berhasil dihapus",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      fetchData();
    } catch (err) {
      toast({
        title: "Gagal menghapus",
        description: err.response?.data?.message || err.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Flex direction="column" pt={{ base: "120px", md: "75px" }}>
      <Card overflowX={{ sm: "scroll", xl: "hidden" }}>
        <CardHeader p="6px 0px 22px 0px" display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={3}>
          <Text fontSize="xl" color={textColor} fontWeight="bold">
            Master Data UPI
          </Text>
          <HStack spacing={3}>
            <InputGroup size="sm" w={{ base: "180px", md: "260px" }}>
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Cari nama UPI / alamat..."
                borderRadius="15px"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
            <Button leftIcon={<AddIcon />} colorScheme="teal" size="sm" borderRadius="10px" onClick={openAdd}>
              Tambah UPI
            </Button>
          </HStack>
        </CardHeader>
        <CardBody>
          {loading ? (
            <Flex justify="center" p={8}>
              <Spinner size="xl" color="teal.500" />
            </Flex>
          ) : (
            <>
              <Table variant="simple" color={textColor}>
                <Thead>
                  <Tr my=".8rem" color="gray.400">
                    <Th color="gray.400">ID</Th>
                    <Th color="gray.400">Nama UPI</Th>
                    <Th color="gray.400">Alamat</Th>
                    <Th color="gray.400">Provinsi</Th>
                    <Th color="gray.400">Kota</Th>
                    <Th color="gray.400">Telepon</Th>
                    <Th color="gray.400">Status</Th>
                    <Th color="gray.400">Aksi</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {currentPagedData.length === 0 ? (
                    <Tr>
                      <Td colSpan={8} textAlign="center" py={6}>
                        {searchTerm ? "Data tidak ditemukan sesuai pencarian" : "Tidak ada data"}
                      </Td>
                    </Tr>
                  ) : (
                    currentPagedData.map((row) => (
                      <Tr key={row.UPI_ID}>
                        <Td>{row.UPI_ID}</Td>
                        <Td fontWeight="bold">{row.UPI_NAMA}</Td>
                        <Td>{row.UPI_ALAMAT || "-"}</Td>
                        <Td>{row.provinsi?.URAIAN_PROPINSI || row.UPI_PROP || "-"}</Td>
                        <Td>{row.kota?.URAIAN_KOTA || row.UPI_KOTA || "-"}</Td>
                        <Td>{row.UPI_TELP || "-"}</Td>
                        <Td>
                          <Badge
                            colorScheme={row.UPI_STATUS === "1" || row.UPI_STATUS === 1 ? "green" : "red"}
                            fontSize="xs"
                            p="3px 10px"
                            borderRadius="8px"
                          >
                            {row.UPI_STATUS === "1" || row.UPI_STATUS === 1 ? "Aktif" : "Non Aktif"}
                          </Badge>
                        </Td>
                        <Td>
                          <IconButton
                            aria-label="edit"
                            icon={<EditIcon />}
                            size="sm"
                            mr={2}
                            colorScheme="blue"
                            variant="ghost"
                            onClick={() => openEdit(row)}
                          />
                          <IconButton
                            aria-label="delete"
                            icon={<DeleteIcon />}
                            size="sm"
                            colorScheme="red"
                            variant="ghost"
                            onClick={() => handleDelete(row.UPI_ID)}
                          />
                        </Td>
                      </Tr>
                    ))
                  )}
                </Tbody>
              </Table>

              {/* Pagination Controls */}
              <Flex justify="space-between" align="center" mt={4} flexWrap="wrap" gap={3}>
                <HStack spacing={2} align="center">
                  <Text fontSize="sm" color="gray.500">
                    Tampilkan
                  </Text>
                  <Select
                    size="sm"
                    w="80px"
                    borderRadius="8px"
                    value={pageSize}
                    onChange={(e) => setPageSize(Number(e.target.value))}
                  >
                    <option value={10}>10</option>
                    <option value={30}>30</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </Select>
                  <Text fontSize="sm" color="gray.500">
                    dari {filteredData.length} data
                  </Text>
                </HStack>

                <HStack spacing={2}>
                  <IconButton
                    aria-label="prev"
                    icon={<ChevronLeftIcon />}
                    size="sm"
                    isDisabled={currentPage <= 1}
                    onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                  />
                  <Text fontSize="sm">
                    {currentPage} / {totalPages}
                  </Text>
                  <IconButton
                    aria-label="next"
                    icon={<ChevronRightIcon />}
                    size="sm"
                    isDisabled={currentPage >= totalPages}
                    onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                  />
                </HStack>
              </Flex>
            </>
          )}
        </CardBody>
      </Card>

      {/* Modal Add/Edit */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editing ? "Ubah Data UPI" : "Tambah Data UPI"}</ModalHeader>
          <ModalCloseButton />
          <form onSubmit={handleSubmit}>
            <ModalBody>
              <FormControl isRequired mb={3}>
                <FormLabel>Nama UPI</FormLabel>
                <Input
                  name="UPI_NAMA"
                  value={form.UPI_NAMA}
                  onChange={handleChange}
                  placeholder="Masukkan Nama UPI"
                />
              </FormControl>
              <FormControl mb={3}>
                <FormLabel>Alamat</FormLabel>
                <Input
                  name="UPI_ALAMAT"
                  value={form.UPI_ALAMAT}
                  onChange={handleChange}
                  placeholder="Masukkan Alamat Lengkap"
                />
              </FormControl>
              <FormControl isRequired mb={3}>
                <FormLabel>Provinsi</FormLabel>
                <Select
                  name="UPI_PROP"
                  value={form.UPI_PROP}
                  onChange={handleChange}
                  placeholder="Pilih Provinsi"
                >
                  {propinsiList.map((p) => (
                    <option key={p.KODE_PROPINSI} value={p.KODE_PROPINSI}>
                      {p.URAIAN_PROPINSI}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl isRequired mb={3}>
                <FormLabel>Kota / Kabupaten</FormLabel>
                <Select
                  name="UPI_KOTA"
                  value={form.UPI_KOTA}
                  onChange={handleChange}
                  placeholder="Pilih Kota / Kabupaten"
                >
                  {filteredKotaOptions.map((k) => (
                    <option key={k.KODE_KOTA} value={k.KODE_KOTA}>
                      {k.URAIAN_KOTA}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl mb={3}>
                <FormLabel>Kode Pos</FormLabel>
                <Input
                  name="UPI_KODEPOS"
                  value={form.UPI_KODEPOS}
                  onChange={handleChange}
                  placeholder="Contoh: 14350"
                  maxLength={10}
                />
              </FormControl>
              <FormControl mb={3}>
                <FormLabel>Telepon</FormLabel>
                <Input
                  name="UPI_TELP"
                  value={form.UPI_TELP}
                  onChange={handleChange}
                  placeholder="Nomor Telepon UPI"
                  maxLength={30}
                />
              </FormControl>
              <FormControl mb={3}>
                <FormLabel>Status UPI</FormLabel>
                <Select
                  name="UPI_STATUS"
                  value={form.UPI_STATUS}
                  onChange={handleChange}
                >
                  <option value="1">Aktif</option>
                  <option value="0">Non Aktif</option>
                </Select>
              </FormControl>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onClose}>
                Batal
              </Button>
              <Button type="submit" colorScheme="teal">
                Simpan
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>
    </Flex>
  );
}
